import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// DELETE /api/admin/deals/:hubspotDealId/account
//
// Replaces /app/admin/deals/_actions.ts::unlinkDealAccount. Optimistic
// field-level patch on the deal detail (Pattern B from bucket 3 /
// MIGRATION.md "Optimistic-update reference"). Patches `account` → null on
// the cached detail row; rolls back the prior value if the worker returns
// 4xx. The cached `accounts` snapshot row stays — other deals may still
// reference it (matches legacy service-side behaviour).
//
// The detail-row type is whatever Phase 7's deal-detail query hook will
// surface; the cache update is structural (set `account: null`) so this
// hook doesn't need to know the full row shape.

type Vars = { hubspotDealId: string };

type Context = {
  previous: unknown;
  key: ReturnType<typeof queryKeys.deals.detail>;
};

export function useUnlinkDealAccount() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars, Context>({
    mutationFn: ({ hubspotDealId }) =>
      apiFetch<{ ok: true }>(`/admin/deals/${hubspotDealId}/account`, {
        method: "DELETE",
      }),
    onMutate: async ({ hubspotDealId }) => {
      const key = queryKeys.deals.detail(hubspotDealId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old: unknown) =>
        old && typeof old === "object"
          ? { ...(old as Record<string, unknown>), account: null }
          : old,
      );
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to unlink account");
    },
    onSettled: (_data, _err, { hubspotDealId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.deals.detail(hubspotDealId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deals.list() });
      // Phase 7 sub-bucket 16 — admin drawer cache. Kanban is unaffected
      // (account snapshot doesn't surface on the cards).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.deals.detail(hubspotDealId),
      });
    },
    onSuccess: () => {
      toast.success("Account unlinked");
    },
  });
}
